// Global internal-mail Compose manager.
//
// Mounting this once (inside the authenticated shell) keeps a single Compose
// window alive across route changes, so a user can MINIMISE a draft, browse the
// portal, and return to finish it. Any page can start a compose/reply via
// ``useCompose().openCompose(initial)``.
//
// On send it fires a ``mail:sent`` window event so pages like Notifications can
// refresh their lists without being coupled to this provider.
import React, { createContext, useCallback, useContext, useState } from 'react';
import ComposeDialog from '@/components/mail/ComposeDialog';

const ComposeCtx = createContext(null);

export const useCompose = () => useContext(ComposeCtx) || { openCompose: () => {} };

export function ComposeProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [initial, setInitial] = useState(null);

  const openCompose = useCallback((init = null) => {
    setInitial(init);
    setOpen(true);
    setMinimized(false);
  }, []);

  const handleOpenChange = useCallback((v) => {
    setOpen(v);
    if (!v) setMinimized(false);
  }, []);

  const onSent = useCallback(() => {
    setMinimized(false);
    try { window.dispatchEvent(new CustomEvent('mail:sent')); } catch { /* noop */ }
  }, []);

  return (
    <ComposeCtx.Provider value={{ openCompose }}>
      {children}
      <ComposeDialog
        open={open}
        minimized={minimized}
        initial={initial}
        onOpenChange={handleOpenChange}
        onMinimize={() => setMinimized(true)}
        onRestore={() => setMinimized(false)}
        onSent={onSent}
      />
    </ComposeCtx.Provider>
  );
}

export default ComposeProvider;
